import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, ExternalLink, Copy, KeyRound, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PREVIEW = (typeof window !== 'undefined' ? window.location.origin : '');

function StripeKeyManager() {
  const [status, setStatus] = useState(null);
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [whMain, setWhMain] = useState('');
  const [whConnect, setWhConnect] = useState('');
  const [savingWh, setSavingWh] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/stripe-setup/status`, { withCredentials: true });
      setStatus(r.data);
    } catch { setStatus({ configured: false }); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const save = async () => {
    if (!key.trim().startsWith('sk_')) return toast.error('Enter a valid secret key (sk_live_… or sk_test_…)');
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/stripe-setup/secret-key`, { secret_key: key.trim() }, { withCredentials: true });
      toast.success(`Saved — ${r.data.mode} mode (${r.data.account_id})`);
      setKey('');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save key');
    } finally { setSaving(false); }
  };

  const saveWebhook = async (which) => {
    const secret = (which === 'connect' ? whConnect : whMain).trim();
    if (!secret.startsWith('whsec_')) return toast.error('Enter a valid signing secret (whsec_…)');
    setSavingWh(which);
    try {
      await axios.post(`${API_URL}/api/admin/stripe-setup/webhook-secret`, { which, secret }, { withCredentials: true });
      toast.success(`${which === 'connect' ? 'Connect' : 'Main'} webhook secret saved`);
      if (which === 'connect') setWhConnect(''); else setWhMain('');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save secret');
    } finally { setSavingWh(''); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="stripe-key-manager">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound size={18} className="text-[#1D4ED8]" />
        <h3 className="font-bold text-[#0A0F1C]">Stripe Secret Key</h3>
      </div>

      {status && (
        status.configured && status.valid ? (
          <div className="flex items-center gap-2 text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 mb-3" data-testid="stripe-key-status-ok">
            <ShieldCheck size={16} />
            <span>Connected — <strong>{status.mode}</strong> mode · key <code className="bg-white px-1 rounded">{status.masked_key}</code> · account {status.account_id}</span>
          </div>
        ) : status.configured && !status.valid ? (
          <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 mb-3" data-testid="stripe-key-status-invalid">
            <AlertTriangle size={16} />
            <span>A key is set (<code className="bg-white px-1 rounded">{status.masked_key || 'placeholder'}</code>) but Stripe rejected it. Paste a valid secret key below.</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 mb-3" data-testid="stripe-key-status-missing">
            <AlertTriangle size={16} />
            <span>No valid Stripe secret key configured. Server-side payments will not work until you add one.</span>
          </div>
        )
      )}

      <p className="text-sm text-[#475569] mb-2">
        Paste your <strong>Secret key</strong> from Stripe Dashboard → Developers → API keys. It's validated against Stripe, stored securely server-side, and never shown again.
      </p>
      <div className="flex gap-2 flex-wrap">
        <input
          type="password"
          className="flex-1 min-w-[260px] px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm font-mono"
          placeholder="sk_live_… or sk_test_…"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          data-testid="stripe-secret-key-input"
          autoComplete="off"
        />
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60" data-testid="stripe-secret-key-save">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}{saving ? 'Validating…' : 'Validate & Save'}
        </button>
      </div>

      {/* Webhook signing secrets */}
      <div className="border-t border-[#E2E8F0] mt-5 pt-4 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div data-testid="wh-main">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-[#0A0F1C]">Main webhook secret</label>
              {status?.webhook_secret_set && <span className="text-[11px] text-green-700 inline-flex items-center gap-1"><ShieldCheck size={12} />{status.webhook_secret_masked}</span>}
            </div>
            <p className="text-xs text-[#94A3B8] mb-2">From Developers → Webhooks → your <code>/api/webhook/stripe-v2</code> endpoint.</p>
            <div className="flex gap-2">
              <input type="password" className="flex-1 min-w-0 px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm font-mono" placeholder="whsec_…" value={whMain} onChange={(e) => setWhMain(e.target.value)} data-testid="wh-main-input" autoComplete="off" />
              <button onClick={() => saveWebhook('main')} disabled={savingWh === 'main'} className="bg-[#0A0F1C] text-white px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60" data-testid="wh-main-save">Save</button>
            </div>
          </div>
          <div data-testid="wh-connect">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-[#0A0F1C]">Connect webhook secret</label>
              {status?.connect_webhook_secret_set && <span className="text-[11px] text-green-700 inline-flex items-center gap-1"><ShieldCheck size={12} />{status.connect_webhook_secret_masked}</span>}
            </div>
            <p className="text-xs text-[#94A3B8] mb-2">From the "Connected accounts" (thin) destination → <code>/api/webhook/stripe-connect</code>.</p>
            <div className="flex gap-2">
              <input type="password" className="flex-1 min-w-0 px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm font-mono" placeholder="whsec_…" value={whConnect} onChange={(e) => setWhConnect(e.target.value)} data-testid="wh-connect-input" autoComplete="off" />
              <button onClick={() => saveWebhook('connect')} disabled={savingWh === 'connect'} className="bg-[#0A0F1C] text-white px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60" data-testid="wh-connect-save">Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminStripeSetup() {
  const copy = async (s) => { try { await navigator.clipboard.writeText(s); toast.success('Copied'); } catch { /* */ } };
  const webhookUrl = `${PREVIEW}/api/webhook/stripe-v2`;

  const steps = [
    {
      title: '1. Verify your account capabilities',
      body: (
        <>
          Your Stripe Dashboard currently shows <strong>multiple capabilities paused</strong> with a past-due verification task. This must be completed before live charges will succeed.
          <a className="ml-2 inline-flex items-center gap-1 text-[#1D4ED8] font-bold" href="https://dashboard.stripe.com/account/onboarding" target="_blank" rel="noopener noreferrer">Open verification <ExternalLink size={12} /></a>
        </>
      ),
    },
    {
      title: '2. Enable Stripe Link (saved-card one-click pay)',
      body: (
        <>
          Go to <a className="text-[#1D4ED8] font-bold" href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noopener noreferrer">Settings → Payment methods <ExternalLink size={12} className="inline" /></a> and toggle <strong>Link</strong> ON. Returning customers will see their saved cards on every Pay Link, the Social Video Manager subscribe page, and any other Stripe Checkout flow on this site. No code change required.
        </>
      ),
    },
    {
      title: '3. Add the webhook endpoint',
      body: (
        <>
          Go to <a className="text-[#1D4ED8] font-bold" href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer">Developers → Webhooks <ExternalLink size={12} className="inline" /></a> → "Add endpoint" → paste this URL:
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <code className="bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1 rounded text-xs font-mono text-[#0A0F1C] break-all">{webhookUrl}</code>
            <button onClick={() => copy(webhookUrl)} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="copy-webhook-url"><Copy size={12} /> Copy</button>
          </div>
          Select these events to send:
          <ul className="list-disc ml-5 mt-2 space-y-1 text-sm">
            <li><code className="bg-[#F8FAFC] px-1 rounded">checkout.session.completed</code></li>
            <li><code className="bg-[#F8FAFC] px-1 rounded">customer.subscription.updated</code></li>
            <li><code className="bg-[#F8FAFC] px-1 rounded">customer.subscription.deleted</code></li>
            <li><code className="bg-[#F8FAFC] px-1 rounded">invoice.paid</code></li>
          </ul>
        </>
      ),
    },
    {
      title: '4. Copy the Webhook Signing Secret',
      body: (
        <>
          After saving the endpoint, click "Reveal" next to <strong>Signing secret</strong>. Copy the <code className="bg-[#F8FAFC] px-1 rounded">whsec_...</code> value and paste it into <code className="bg-[#F8FAFC] px-1 rounded">backend/.env</code> as <code className="bg-[#F8FAFC] px-1 rounded">STRIPE_WEBHOOK_SECRET</code>, then restart the backend.
        </>
      ),
    },
    {
      title: '5. (Optional) Add Slack notifications',
      body: (
        <>
          Create an incoming webhook in your Slack workspace at <a className="text-[#1D4ED8] font-bold" href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer">api.slack.com/messaging/webhooks <ExternalLink size={12} className="inline" /></a>, copy the URL, and set <code className="bg-[#F8FAFC] px-1 rounded">SLACK_WEBHOOK_URL</code> in <code className="bg-[#F8FAFC] px-1 rounded">backend/.env</code>. Every new booking, payment, and testimonial submission will ping Slack instantly.
        </>
      ),
    },
    {
      title: '6. Test with a real $1 charge (recommended)',
      body: (
        <>
          Go to <strong>/admin/payment-links</strong>, generate a link for any paid program, open it on your phone (so Link can fingerprint your card), pay $1, and confirm:
          <ul className="list-disc ml-5 mt-2 space-y-1 text-sm">
            <li>You receive the branded receipt email.</li>
            <li>The enrollment shows up under <strong>/admin/programs → Enrollments</strong>.</li>
            <li>The webhook event appears under Stripe Dashboard → Webhooks → recent deliveries with HTTP 200.</li>
            <li>(Optional) Slack pings the channel.</li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <AdminLayout title="Stripe Setup Guide">
      <div className="max-w-3xl space-y-4">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#F97316] text-white rounded-xl p-6">
          <h1 className="text-2xl font-bold mb-1" data-testid="stripe-setup-title">10-minute Stripe production checklist</h1>
          <p className="text-white/85 text-sm">Add your secret key below, then complete these steps inside the Stripe Dashboard to turn the integration on for production.</p>
        </div>

        <StripeKeyManager />

        {steps.map((s, i) => (
          <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`stripe-setup-step-${i + 1}`}>
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-[#1D4ED8] flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="font-bold text-[#0A0F1C] mb-1">{s.title}</h3>
                <div className="text-sm text-[#475569] leading-relaxed">{s.body}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
